<template>
  <div class="spm-Wrapper">
    <FormCaption :subtitle="t(subtitle)" :title="t(title)">
      <div class="spm-Toolbar">
        <FormFieldNext>
          <AppRadioGroup
            v-model="formModel[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.STRATEGY_TYPE_ID]"
            :data-auto-testid="PARTICIPANTS_STRATEGY_FIELD_TEST_ID"
            :data-testid="PARTICIPANTS_STRATEGY_FIELD_TEST_ID"
            :options="strategies"
            :type="RADIO_GROUP_TYPES.LIST"
            full-width
            name="participants-strategy"
            @update:model-value="onUpdateStrategy"
          />
        </FormFieldNext>
      </div>
    </FormCaption>
    <template v-if="!isAllUsersStrategy">
      <div class="spm-Segments">
        <div class="spm-Segments_Label">
          {{ t('action.include') }}
        </div>
        <div class="spm-Segments_Fields">
          <ScheduleParticipantsSelect
            v-model="formModel[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.GROUP_IDS]"
            :data-auto-testid="GROUPS_FIELD_TEST_ID"
            :data-testid="GROUPS_FIELD_TEST_ID"
            :is-error="validationErrors.isSegmentsEmpty"
            :item-label="GLOBAL_GROUP_KEYS.NAME"
            :item-value="GLOBAL_GROUP_KEYS.ID"
            :loading="groupsForAddLoading"
            :options="groupsForAddList"
            :search-function="fetchGroupsForAdd"
            empty-state-icon="team-next-small"
            @open="removeError('isSegmentsEmpty')"
            @update:options="groupsForAddList = $event"
            @toggle-dropdown="openedDropdowns[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.GROUP_IDS] = $event"
          >
            <template #option-label="{ option }">
              <GlobalGroupsSelectOption v-if="option" :group="option" />
            </template>

            <template #empty-state-label>
              {{ t('user_details.select_groups') }}
            </template>

            <template #error>
              <AppFieldError show>
                {{ t('schedule.participants.empty_segments_error') }}
              </AppFieldError>
            </template>
          </ScheduleParticipantsSelect>

          <ScheduleParticipantsSelect
            v-model="formModel[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.USER_ACCOUNT_IDS]"
            :data-auto-testid="USERS_FIELD_TEST_ID"
            :data-testid="USERS_FIELD_TEST_ID"
            :is-error="validationErrors.isSegmentsEmpty"
            :item-label="ASSIGNEE_ENTITY_KEYS.NAME"
            :item-value="ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID"
            :loading="usersForAddLoading"
            :options="usersForAddList"
            :search-function="fetchUsersForAdd"
            empty-state-icon="user-next-small"
            @open="removeError('isSegmentsEmpty')"
            @update:options="usersForAddList = $event"
            @update:model-value="onUpdateUserSegments"
            @toggle-dropdown="
              openedDropdowns[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.USER_ACCOUNT_IDS] = $event
            "
          >
            <template #option-label="{ option }">
              <OwnerFieldOption :label-key="ASSIGNEE_ENTITY_KEYS.NAME" :option="option" />
            </template>

            <template #empty-state-label>
              {{ t('action.select_user', 0) }}
            </template>

            <template #error>
              <AppFieldError show>
                {{ t('schedule.participants.empty_segments_error') }}
              </AppFieldError>
            </template>
          </ScheduleParticipantsSelect>
        </div>
      </div>

      <AppDivider style="--divider-offset: 12px 0; --divider-border-style: dashed" />
    </template>

    <div
      ref="filterFields"
      :class="{
        'spm-Filter-one-line': isFiltersFitToOneLine
      }"
      :data-auto-testid="FILTERS_TABLE_TEST_ID"
      :data-testid="FILTERS_TABLE_TEST_ID"
      class="spm-Filter"
    >
      <div class="spm-Filter_Label">{{ t('action.exclude') }}</div>
      <div class="spm-Filter_Fields">
        <ScheduleParticipantsSelect
          v-model="formModel[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_USER_ACCOUNT_IDS]"
          :data-auto-testid="EXCLUDED_USERS_FIELD_TEST_ID"
          :data-testid="EXCLUDED_USERS_FIELD_TEST_ID"
          :hidden-items="hiddenItemsForUserExclusion"
          :item-label="ASSIGNEE_ENTITY_KEYS.NAME"
          :item-value="ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID"
          :loading="excludeUsersLoading"
          :options="usersForExclusionList"
          :search-function="fetchUsersForExclusion"
          empty-state-icon="user-next-small"
          @update:options="usersForExclusionList = $event"
          @toggle-dropdown="
            openedDropdowns[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_USER_ACCOUNT_IDS] = $event
          "
        >
          <template #option-label="{ option }">
            <OwnerFieldOption :label-key="ASSIGNEE_ENTITY_KEYS.NAME" :option="option" />
          </template>

          <template #empty-state-label>
            {{ t('action.select_user', 0) }}
          </template>
        </ScheduleParticipantsSelect>

        <ScheduleParticipantsSelect
          v-if="isAllUsersStrategy"
          v-model="formModel[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_GROUP_IDS]"
          :data-auto-testid="EXCLUDED_GROUPS_FIELD_TEST_ID"
          :data-testid="EXCLUDED_GROUPS_FIELD_TEST_ID"
          :item-label="GLOBAL_GROUP_KEYS.NAME"
          :item-value="GLOBAL_GROUP_KEYS.ID"
          :loading="excludeGroupsLoading"
          :options="groupsForExclusionList"
          :search-function="fetchGroupsForExclusion"
          empty-state-icon="team-next-small"
          @open="removeError('isSegmentsEmpty')"
          @update:options="groupsForExclusionList = $event"
          @toggle-dropdown="
            openedDropdowns[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_GROUP_IDS] = $event
          "
        >
          <template #option-label="{ option }">
            <GlobalGroupsSelectOption v-if="option" :group="option" />
          </template>

          <template #empty-state-label>
            {{ t('user_details.select_groups') }}
          </template>
        </ScheduleParticipantsSelect>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useResizeObserver } from '@vueuse/core'
import { cloneDeep, isEmpty } from 'lodash'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useFetchGroups, useFetchUsers } from '@/utils/check-ins/schedule-composables'
import {
  DEFAULT_SCHEDULE_PARTICIPANTS,
  STRATEGY_OPTIONS
} from '@/utils/check-ins/schedules-participants'
import { RADIO_GROUP_TYPES } from '@/utils/components-configurations/app-radio-group'
import { ASSIGNEE_ENTITY_KEYS, SCHEDULE_PARTICIPANTS_ENTITY_KEYS } from '@/utils/entity-keys'
import { GLOBAL_GROUP_KEYS } from '@/utils/global-groups'

import FormCaption from '@/components/check-ins/FormCaption'
import {
  EXCLUDED_GROUPS_FIELD_TEST_ID,
  EXCLUDED_USERS_FIELD_TEST_ID,
  FILTERS_TABLE_TEST_ID,
  GROUPS_FIELD_TEST_ID,
  PARTICIPANTS_STRATEGY_FIELD_TEST_ID,
  USERS_FIELD_TEST_ID
} from '@/components/check-ins/jest-helpers'
import ScheduleParticipantsSelect from '@/components/check-ins/ScheduleParticipantsSelect'
import AppFieldError from '@/components/form/AppFieldError'
import FormFieldNext from '@/components/form/FormFieldNext'
import OwnerFieldOption from '@/components/form/OwnerFieldOption'
import GlobalGroupsSelectOption from '@/components/global-groups/GlobalGroupsSelectOption'
import AppDivider from '@/components/ui/AppDivider'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'

defineOptions({
  name: 'ScheduleParticipantsManagement'
})

const formModel = defineModel({
  type: Object,
  required: true,
  set: value => value
})

defineProps({
  validationErrors: {
    type: Object,
    default: () => ({
      isSegmentsEmpty: false
    })
  },

  subtitle: {
    type: String,
    default: 'schedule.participants.subtitle'
  },

  title: {
    type: String,
    default: 'schedule.participants.title'
  }
})

const { t } = useI18n()

const usersForExclusionList = ref([])

const { doFetch: fetchUsersForExclusion, loading: excludeUsersLoading } = useFetchUsers({
  selectedUsers: () => formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_USER_ACCOUNT_IDS]
})

const groupsForExclusionList = ref([])

const { doFetch: fetchGroupsForExclusion, loading: excludeGroupsLoading } = useFetchGroups({
  selectedGroups: () => formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_GROUP_IDS]
})

const groupsForAddList = ref([])

const { doFetch: fetchGroupsForAdd, loading: groupsForAddLoading } = useFetchGroups({
  selectedGroups: () => formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.GROUP_IDS]
})

const usersForAddList = ref([])

const { doFetch: fetchUsersForAdd, loading: usersForAddLoading } = useFetchUsers({
  selectedUsers: () => formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.USER_ACCOUNT_IDS]
})

const setListsForFilters = async () => {
  const setUsersFilter = async () => {
    usersForExclusionList.value = await fetchUsersForExclusion()
  }

  const setGroupsFilter = async () => {
    groupsForExclusionList.value = await fetchGroupsForExclusion()
  }

  const promises = []

  if (isEmpty(usersForExclusionList.value)) {
    promises.push(setUsersFilter())
  }

  if (isEmpty(groupsForExclusionList.value) && isAllUsersStrategy.value) {
    promises.push(setGroupsFilter())
  }

  await Promise.all(promises)
}

const setListsForSegmentStrategy = async () => {
  const setUsersForAdd = async () => {
    usersForAddList.value = await fetchUsersForAdd()
  }

  const setGroupsForAdd = async () => {
    groupsForAddList.value = await fetchGroupsForAdd()
  }

  const promises = []

  if (isEmpty(usersForAddList.value)) {
    promises.push(setUsersForAdd())
  }

  if (isEmpty(groupsForAddList.value)) {
    promises.push(setGroupsForAdd())
  }

  await Promise.all(promises)
}

const onUpdateStrategy = async newStrategy => {
  onRemoveAllFilters()

  if (newStrategy === STRATEGY_OPTIONS.SELECTED_SEGMENT[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.VALUE]) {
    await setListsForSegmentStrategy()
  }
}

const onRemoveAllFilters = () => {
  formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_USER_ACCOUNT_IDS] = cloneDeep(
    DEFAULT_SCHEDULE_PARTICIPANTS[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_USER_ACCOUNT_IDS]
  )

  formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_GROUP_IDS] = cloneDeep(
    DEFAULT_SCHEDULE_PARTICIPANTS[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_GROUP_IDS]
  )
}

const isAllUsersStrategy = computed(() => {
  return (
    formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.STRATEGY_TYPE_ID] ===
    STRATEGY_OPTIONS.ALL_USERS[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.VALUE]
  )
})

const hiddenItemsForUserExclusion = computed(() => {
  return isAllUsersStrategy.value
    ? []
    : formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.USER_ACCOUNT_IDS]
})

const onUpdateUserSegments = newValue => {
  formModel.value[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_USER_ACCOUNT_IDS] = formModel.value[
    SCHEDULE_PARTICIPANTS_ENTITY_KEYS.EXCLUDED_USER_ACCOUNT_IDS
  ].filter(id => !newValue.includes(id))
}

const emit = defineEmits({
  'remove-error': null
})

const removeError = key => {
  emit('remove-error', key)
}

watch(
  () => formModel.value,
  async (newValue, oldValue) => {
    if (newValue && !oldValue) {
      const promises = [setListsForFilters()]

      const isSelectedSegmentStrategy =
        newValue[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.STRATEGY_TYPE_ID] ===
        STRATEGY_OPTIONS.SELECTED_SEGMENT[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.VALUE]

      if (isSelectedSegmentStrategy) {
        promises.push(setListsForSegmentStrategy())
      }

      await Promise.all(promises)
    }
  },
  { deep: true, immediate: true }
)

const strategies = computed(() => {
  return Object.values(STRATEGY_OPTIONS).map(option => {
    return {
      ...option,
      label: t(option[SCHEDULE_PARTICIPANTS_ENTITY_KEYS.LABEL])
    }
  })
})

const isSomeDropdownOpenedModel = defineModel('isSomeDropdownOpened', {
  type: Boolean,
  required: true,
  set: value => value
})

const openedDropdowns = ref({})

watch(
  openedDropdowns,
  (newValue = {}) => {
    isSomeDropdownOpenedModel.value = Object.values(newValue).some(Boolean)
  },
  {
    deep: true
  }
)

const filterFieldsRef = useTemplateRef('filterFields')

const isFiltersFitToOneLine = ref(true)

useResizeObserver(filterFieldsRef, entries => {
  isFiltersFitToOneLine.value = entries[0].contentRect.height <= 40
})
</script>

<style lang="scss" scoped>
.spm-Toolbar {
  max-width: 100%;
  --option-padding: 8px 12px;
  --option-font-size: #{$fs-14};
  margin-bottom: 20px;
}

.spm-Filter,
.spm-Segments {
  display: flex;
  gap: 20px;
}

.spm-Filter-one-line {
  align-items: center;
}

.spm-Filter_Label,
.spm-Segments_Label {
  width: 90px;
  flex-shrink: 0;
  color: $dark-2;
  font-size: $fs-14;
  line-height: 20px;
}

.spm-Filter_Fields,
.spm-Segments_Fields {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
